import React, { useState, useRef, useEffect } from 'react';
import sendMessageToBot from './api';
import styles from './chatWidget.module.css';
//assets
import botImg from '../../assets/publicbot/bot.png';
import msgIcon from "../../assets/publicbot/messenger.svg";
import closeIcon from "../../assets/publicbot/close.svg";
import sendIcon from "../../assets/publicbot/send.svg";
import clearIcon from "../../assets/publicbot/Clear.svg";

interface Message {
  id?: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp?: Date;
}

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Welcome to Optiorx! How can I help you today?',
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateMessageId = (): string => {
    return Math.random().toString(36).substring(7);
  };

  const clearChat = (): void => {
    setMessages([
      {
        id: '1',
        text: 'Welcome to Optiorx! How can I help you today?',
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    
    e.preventDefault();

    if (!inputMessage.trim()) return;

    const newMessage: Message = {
      id: generateMessageId(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');

    setIsLoading(true);

    try {
      const data = {
        "text": inputMessage,
        "user_id":"public",
        "session_id":"1"
      }
      const botResponse = await sendMessageToBot(data);
      
      // await new Promise(resolve => setTimeout(resolve, 5000));

      const botMsg: Message = {
        id: generateMessageId(),
        text: botResponse.response_text,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMsg]);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        id: generateMessageId(),
        text: 'Sorry, there was an error processing your message.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.chatContainer}>
      {!isOpen && (
        <button
          className={styles.chatToggle}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle chat"
        >
          <div className={styles.profileContainer}>
            <img
              src={botImg}
              alt="Support Agent"
              className={styles.profileImage}
            />
            <div className={styles.statusIndicator} />
            <div className={styles.messengerIcon}>
              <img src={msgIcon} alt="Messenger Icon" />
            </div>
          </div>
        </button>
      )}

      {isOpen && (
        <div className={styles.chatWindow}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.botOnline}></div>
              <p>Hi, this is Mia!</p>
            </div>

            <div className={styles.clearAll}>
              {messages.length > 1 && (
                <div onClick={clearChat}>
                  <img src={clearIcon} alt="Clear" />
                  <button className={styles.clearAllButton}>Clear All</button>
                </div>
              )}
              <button
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                <img src={closeIcon} alt="Close" />
              </button>
            </div>

          </div>

          <div className={styles.messagesContainer}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`${styles.messageContainer} ${message.sender === 'user' ? styles.userContainer : styles.botContainer}`}
              >
                {/* Bot Profile (Left) */}
                {message.sender === 'bot' && (
                  <img
                    src={botImg}
                    alt="bot"
                    className={styles.chatProfile}
                  />
                )}

                {/* Message Text */}
                <div
                  className={`${styles.message} ${message.sender === 'user' ? styles.userMessage : styles.botMessage}`}
                >
                  {message.text}
                </div>

                {/* User Profile (Right) */}
                {message.sender === 'user' && (
                  <div className={styles.userProfile}>
                    <img
                      src={msgIcon}
                      alt="user"
                      className={styles.userIcon}
                    />
                  </div>
                )}
              </div>
            ))}

            {/* Loading Indicator */}
            {isLoading && (
              <div className={`${styles.messageContainer} ${styles.botContainer}`}>
                <img
                  src={botImg}
                  alt="bot"
                  className={styles.chatProfile}
                />
                <div className={styles.typing}>
                  <div className={styles.dot} />
                  <div className={styles.dot} />
                  <div className={styles.dot} />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className={styles.inputContainer}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Enter your Message..."
              className={styles.input}
              disabled={isLoading}
            />
            <button
              type="submit"
              className={styles.sendButton}
              disabled={isLoading || !inputMessage.trim()}
            >
              <img src={sendIcon} alt="Send" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;